import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useAuth } from '../store';
import { isUltraAdmin } from '../utils';
import { saveTemplate } from '../services/firestore';
import type { Event } from '../types';

/**
 * Create Template Page (Ultra Admin Only)
 * Create a new event template
 */
const SPORTS = ['UFC', 'Boxing', 'Bellator', 'PFL', 'ONE Championship', 'Other'];

export function CreateTemplatePage() {
  const [templateName, setTemplateName] = useState('');
  const [sport, setSport] = useState('UFC');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Check ultra-admin access
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !currentUser || !isUltraAdmin(currentUser))) {
      navigate('/');
      return;
    }
  }, [isAuthenticated, currentUser, authLoading, navigate]);

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-secondary-text">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not ultra-admin
  if (!isAuthenticated || !currentUser || !isUltraAdmin(currentUser)) {
    return null;
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }

    if (!currentUser) {
      setError('You must be logged in to create a template');
      return;
    }

    setLoading(true);

    try {
      // Create template event (without participants)
      const templateId = `template-${Date.now()}`;
      const template: Event = {
        id: templateId,
        name: templateName.trim(),
        createdAt: Date.now(),
        status: 'upcoming',
        finished: false,
        participants: [],
        fights: [],
        shareableLink: '', // Templates don't need shareable links
        adminId: currentUser.id,
        adminUsername: currentUser.username,
        isPublic: false,
        published: false, // New templates start as drafts
        isTemplate: true,
        templateUsageCount: 0,
        sport: sport,
        isAvailableForEvents: true, // Available by default (but won't show until published)
        hideCompletedFights: false, // Show all fights by default
      };
      
      // Save template to Firestore
      await saveTemplate(template);
      
      console.log('✅ Template created successfully:', template.id);
      
      // Navigate to admin page to add fights
      navigate(`/admin/${template.id}`);
    } catch (err) {
      setError('Failed to create template. Please try again.');
      console.error('Error creating template:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-text mb-2">
            Create Template
          </h1>
          <p className="text-secondary-text text-lg">
            Create a reusable fight card template
          </p>
          <p className="text-sm text-muted-text mt-2">
            After creating, you'll add fights and odds
          </p>
        </div>

        <Card padding="lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Template Name"
              placeholder="e.g., UFC 322, Bellator 301"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              error={error}
              helperText="Give this template a descriptive name"
              autoFocus
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-primary-text">
                Sport
              </label>
              <select
                value={sport}
                onChange={(e) => setSport(e.target.value)}
                className="w-full px-3 py-2 bg-secondary-bg border border-border rounded-lg text-primary-text focus:outline-none focus:ring-2 focus:ring-primary-blue"
              >
                {SPORTS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <p className="text-xs text-secondary-text">Select the sport for this template</p>
            </div>

            <Button 
              type="submit" 
              variant="primary" 
              fullWidth
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Template'}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => navigate('/ultra-admin/templates')}
            >
              Back to Templates
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
